// @ts-nocheck
import { toast } from "@zerodevx/svelte-toast";
import {
  PUBLIC_BASE_API_URL,
  PUBLIC_UZAKITU_TINYPESA_API_KEY,
  PUBLIC_VITE_API_PROXY_TARGET,
} from "$env/static/public";

// Use proxy in development, direct URL in production
// Reads from environment variable using SvelteKit's proper env module
export const BASE_API_URL =
  PUBLIC_BASE_API_URL || "https://api.uzakitu.com/api/";

// TinyPesa API Key - reads from environment variable
// WARNING: This is exposed to the client-side. Consider moving to server-side if possible.
export const UZAKITU_TINYPESA_API_KEY = PUBLIC_UZAKITU_TINYPESA_API_KEY || "";

// API Proxy Target
export const VITE_API_PROXY_TARGET =
  PUBLIC_VITE_API_PROXY_TARGET || "https://api.uzakitu.com";

export function dateFormater(date) {
  const utcDate = new Date(date);
  const formattedDate = utcDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
  return formattedDate.replace(/\//g, "-");
}

export function alertSuccesMessage(msg) {
  toast.push(
    `<div style="display:flex;align-items:center;gap:10px">
       <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#16a34a" stroke-width="2.5" style="flex-shrink:0">
         <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
       </svg>
       <span style="font-size:0.875rem;font-weight:500;color:#14532d">${msg}</span>
     </div>`,
    {
      dismissable: true,
      duration: 4000,
      theme: {
        "--toastBackground": "#f0fdf4",
        "--toastColor": "#14532d",
        "--toastBarBackground": "#16a34a",
        "--toastBorderRadius": "10px",
        "--toastPadding": "0.75rem 1rem",
        "--toastBoxShadow": "0 4px 12px rgba(0,0,0,0.12)",
        "--toastBarHeight": "3px",
        "--toastWidth": "340px",
        "--toastMinHeight": "auto",
      },
    },
  );
}

export function alertErrorMessage(msg) {
  toast.push(
    `<div style="display:flex;align-items:center;gap:10px">
       <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#dc2626" stroke-width="2.5" style="flex-shrink:0">
         <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
       </svg>
       <span style="font-size:0.875rem;font-weight:500;color:#7f1d1d">${msg}</span>
     </div>`,
    {
      dismissable: true,
      duration: 5000,
      theme: {
        "--toastBackground": "#fff1f2",
        "--toastColor": "#7f1d1d",
        "--toastBarBackground": "#dc2626",
        "--toastBorderRadius": "10px",
        "--toastPadding": "0.75rem 1rem",
        "--toastBoxShadow": "0 4px 12px rgba(0,0,0,0.12)",
        "--toastBarHeight": "3px",
        "--toastWidth": "340px",
        "--toastMinHeight": "auto",
      },
    },
  );
}

/**
 * Get image URL with fallback options
 * Tries multiple possible field names from the API response
 * @param {Object} imageData - The image data object from API
 * @param {string} preferredQuality - 'original' or 'resized' (default: 'resized' - more reliable)
 * @returns {string} - The image URL or empty string if none found
 */
export function getImageUrl(imageData, preferredQuality = 'resized') {
  if (!imageData) return '';

  // Prioritize resized URLs as they're more reliable (CDN cached)
  // Try resized first, then original, then direct file URLs
  const urlFields = preferredQuality === 'resized'
    ? ['resized_file_url', 'original_file_url', 'file_url', 'resized_file', 'original_file', 'file']
    : ['original_file_url', 'resized_file_url', 'file_url', 'original_file', 'resized_file', 'file'];

  // Try each field in order
  for (const field of urlFields) {
    if (imageData[field] && typeof imageData[field] === 'string' && imageData[field].trim() !== '') {
      return imageData[field];
    }
  }

  return '';
}

/**
 * Get all possible image URLs from imageData for fallback purposes
 * @param {Object} imageData - The image data object from API
 * @returns {Array<string>} - Array of all valid image URLs found
 */
export function getAllImageUrls(imageData) {
  if (!imageData) return [];

  const urlFields = ['resized_file_url', 'original_file_url', 'file_url', 'resized_file', 'original_file', 'file'];
  const urls = [];

  for (const field of urlFields) {
    if (imageData[field] && typeof imageData[field] === 'string' && imageData[field].trim() !== '') {
      // Avoid duplicates
      if (!urls.includes(imageData[field])) {
        urls.push(imageData[field]);
      }
    }
  }

  return urls;
}

/**
 * Get product image URL with fallbacks
 * Products have structure: product.product_images[0].image_data
 * For products, resized_file_url works most reliably (CDN cached)
 * @param {Object} product - The product object
 * @param {number} imageIndex - Index of the image to get (default: 0)
 * @returns {string} - The image URL or empty string if none found
 */
export function getProductImageUrl(product, imageIndex = 0) {
  if (!product?.product_images || product.product_images.length === 0) {
    return '';
  }

  const imageObj = product.product_images[imageIndex];
  if (!imageObj?.image_data) return '';

  const imageData = imageObj.image_data;

  // Product-specific priority order: resized_file_url works best (CDN cached)
  const urlFields = ['resized_file_url', 'original_file_url', 'file_url', 'resized_file', 'original_file', 'file'];

  for (const field of urlFields) {
    if (imageData[field] && typeof imageData[field] === 'string' && imageData[field].trim() !== '') {
      return imageData[field];
    }
  }

  return '';
}

/**
 * Get all possible product image URLs for fallback purposes
 * @param {Object} product - The product object
 * @param {number} imageIndex - Index of the image to get (default: 0)
 * @returns {Array<string>} - Array of all valid image URLs found
 */
export function getAllProductImageUrls(product, imageIndex = 0) {
  if (!product?.product_images || product.product_images.length === 0) {
    return [];
  }

  const imageObj = product.product_images[imageIndex];
  if (!imageObj?.image_data) return [];

  const imageData = imageObj.image_data;

  // Product-specific priority order
  const urlFields = ['resized_file_url', 'original_file_url', 'file_url', 'resized_file', 'original_file', 'file'];
  const urls = [];

  for (const field of urlFields) {
    if (imageData[field] && typeof imageData[field] === 'string' && imageData[field].trim() !== '') {
      if (!urls.includes(imageData[field])) {
        urls.push(imageData[field]);
      }
    }
  }

  return urls;
}

/**
 * Get category image URL with fallbacks
 * Categories have structure: category.image_data[0].image
 * For categories, resized_file_url works most reliably
 * @param {Object} category - The category object
 * @param {number} imageIndex - Index of the image to get (default: 0)
 * @returns {string} - The image URL or empty string if none found
 */
export function getCategoryImageUrl(category, imageIndex = 0) {
  if (!category?.image_data || category.image_data.length === 0) {
    return '';
  }

  const imageObj = category.image_data[imageIndex];
  if (!imageObj?.image) return '';

  const imageData = imageObj.image;

  // Category-specific priority order: resized_file_url works best
  const urlFields = ['resized_file_url', 'original_file_url', 'file_url', 'resized_file', 'original_file', 'file'];

  for (const field of urlFields) {
    if (imageData[field] && typeof imageData[field] === 'string' && imageData[field].trim() !== '') {
      return imageData[field];
    }
  }

  return '';
}

/**
 * Get all possible category image URLs for fallback purposes
 * @param {Object} category - The category object
 * @param {number} imageIndex - Index of the image to get (default: 0)
 * @returns {Array<string>} - Array of all valid image URLs found
 */
export function getAllCategoryImageUrls(category, imageIndex = 0) {
  if (!category?.image_data || category.image_data.length === 0) {
    return [];
  }

  const imageObj = category.image_data[imageIndex];
  if (!imageObj?.image) return [];

  const imageData = imageObj.image;

  // Category-specific priority order: resized_file_url works best
  const urlFields = ['resized_file_url', 'original_file_url', 'file_url', 'resized_file', 'original_file', 'file'];
  const urls = [];

  for (const field of urlFields) {
    if (imageData[field] && typeof imageData[field] === 'string' && imageData[field].trim() !== '') {
      if (!urls.includes(imageData[field])) {
        urls.push(imageData[field]);
      }
    }
  }

  return urls;
}

/**
 * Get brand image URL with fallbacks
 * Brands have structure: brand.brand_images[0].images
 * @param {Object} brand - The brand object
 * @param {number} imageIndex - Index of the image to get (default: 0)
 * @returns {string} - The image URL or empty string if none found
 */
export function getBrandImageUrl(brand, imageIndex = 0) {
  if (!brand?.brand_images || brand.brand_images.length === 0) {
    return '';
  }

  const imageObj = brand.brand_images[imageIndex];
  if (!imageObj?.images) return '';

  const imageData = imageObj.images;

  // Brand-specific priority order: try original first, then file_url
  const urlFields = ['original_file_url', 'file_url', 'original_file', 'resized_file_url', 'resized_file', 'file'];

  for (const field of urlFields) {
    if (imageData[field] && typeof imageData[field] === 'string' && imageData[field].trim() !== '') {
      return imageData[field];
    }
  }

  return '';
}

/**
 * Get all possible brand image URLs for fallback purposes
 * @param {Object} brand - The brand object
 * @param {number} imageIndex - Index of the image to get (default: 0)
 * @returns {Array<string>} - Array of all valid image URLs found
 */
export function getAllBrandImageUrls(brand, imageIndex = 0) {
  if (!brand?.brand_images || brand.brand_images.length === 0) {
    return [];
  }

  const imageObj = brand.brand_images[imageIndex];
  if (!imageObj?.images) return [];

  const imageData = imageObj.images;

  // Brand-specific priority order
  const urlFields = ['original_file_url', 'file_url', 'original_file', 'resized_file_url', 'resized_file', 'file'];
  const urls = [];

  for (const field of urlFields) {
    if (imageData[field] && typeof imageData[field] === 'string' && imageData[field].trim() !== '') {
      if (!urls.includes(imageData[field])) {
        urls.push(imageData[field]);
      }
    }
  }

  return urls;
}

/**
 * Get blog image URL with fallbacks
 * Blogs have structure: blog.blog_images[0].images
 * The 'images' field can be a direct URL string or an object with URL fields
 * @param {Object} blog - The blog object
 * @param {number} imageIndex - Index of the image to get (default: 0)
 * @returns {string} - The image URL or empty string if none found
 */
export function getBlogImageUrl(blog, imageIndex = 0) {
  if (!blog?.blog_images || blog.blog_images.length === 0) {
    return '';
  }

  const imageObj = blog.blog_images[imageIndex];
  if (!imageObj) return '';

  // Check if 'images' is a direct URL string
  if (imageObj.images && typeof imageObj.images === 'string' && imageObj.images.trim() !== '') {
    return imageObj.images;
  }

  // If 'images' is an object, try to get URLs from it
  const imageData = imageObj.images || imageObj;

  // Blog-specific priority order
  const urlFields = ['original_file_url', 'resized_file_url', 'file_url', 'original_file', 'resized_file', 'file'];

  for (const field of urlFields) {
    if (imageData[field] && typeof imageData[field] === 'string' && imageData[field].trim() !== '') {
      return imageData[field];
    }
  }

  return '';
}

/**
 * Get all possible blog image URLs for fallback purposes
 * @param {Object} blog - The blog object
 * @param {number} imageIndex - Index of the image to get (default: 0)
 * @returns {Array<string>} - Array of all valid image URLs found
 */
export function getAllBlogImageUrls(blog, imageIndex = 0) {
  if (!blog?.blog_images || blog.blog_images.length === 0) {
    return [];
  }

  const imageObj = blog.blog_images[imageIndex];
  if (!imageObj) return [];

  const urls = [];

  // Check if 'images' is a direct URL string
  if (imageObj.images && typeof imageObj.images === 'string' && imageObj.images.trim() !== '') {
    urls.push(imageObj.images);
  }

  // If 'images' is an object, try to get URLs from it
  const imageData = imageObj.images || imageObj;

  // Blog-specific priority order
  const urlFields = ['original_file_url', 'resized_file_url', 'file_url', 'original_file', 'resized_file', 'file'];

  for (const field of urlFields) {
    if (imageData[field] && typeof imageData[field] === 'string' && imageData[field].trim() !== '') {
      if (!urls.includes(imageData[field])) {
        urls.push(imageData[field]);
      }
    }
  }

  return urls;
}
